import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import {
  buildInboxKpis,
  sortInboxDefects,
  type InboxDefect,
} from "@/components/dashboard/roleDashboard.helpers";

/**
 * Mängel-Inbox-Daten für das mangelbearbeiter-Dashboard (TES-1093):
 * mir zugewiesene offene Mängel (open/in_progress, nach Schwere/Alter)
 * plus die zuletzt gelösten (resolved/closed) — reale defect_status-Enums,
 * kein "verified" (D-CHK-015).
 */

const defectInboxKeys = {
  all: ["defect-inbox"] as const,
  list: (userId?: string) => [...defectInboxKeys.all, userId] as const,
};

const INBOX_SELECT = "id, title, severity, status, due_date, created_at, updated_at, resolved_at";

interface DefectInboxData {
  open: InboxDefect[];
  recentlyResolved: InboxDefect[];
}

const EMPTY_INBOX: DefectInboxData = { open: [], recentlyResolved: [] };

/** PostgREST-Resultat auspacken: Fehler werfen, null-Daten zu leerer Liste glätten. */
function unwrapRows(result: {
  data: unknown[] | null;
  error: { message: string } | null;
}): InboxDefect[] {
  if (result.error) throw result.error;
  return (result.data ?? []) as InboxDefect[];
}

async function fetchDefectInbox(userId: string): Promise<DefectInboxData> {
  const [openResult, resolvedResult] = await Promise.all([
    supabase
      .from("defects")
      .select(INBOX_SELECT)
      .is("deleted_at", null)
      .eq("assigned_to", userId)
      .in("status", ["open", "in_progress"])
      .order("created_at", { ascending: true })
      .limit(50),
    supabase
      .from("defects")
      .select(INBOX_SELECT)
      .is("deleted_at", null)
      .eq("assigned_to", userId)
      .in("status", ["resolved", "closed"])
      .order("updated_at", { ascending: false })
      .limit(5),
  ]);

  return {
    open: sortInboxDefects(unwrapRows(openResult)),
    recentlyResolved: unwrapRows(resolvedResult),
  };
}

export function useDefectInboxQuery(userId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: defectInboxKeys.list(userId),
    queryFn: () => fetchDefectInbox(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Realtime: Mangel-Änderungen invalidieren die Inbox (zentraler Push).
  useRealtimeSubscription(
    ["defects"],
    () => {
      queryClient.invalidateQueries({ queryKey: defectInboxKeys.all });
    },
    { enabled: !!userId, debounceMs: 1000 },
  );

  const data = query.data ?? EMPTY_INBOX;

  return {
    open: data.open,
    recentlyResolved: data.recentlyResolved,
    kpis: buildInboxKpis(data.open),
    isLoading: query.isLoading,
    error: query.error,
  };
}
